/**
 * API Client
 *
 * Centralized HTTP client with automatic token management and refresh.
 * Eliminates duplicated token refresh logic across all pages.
 *
 * Features:
 * - Automatic token injection in request headers
 * - Automatic token refresh on 498 (token expired)
 * - Request/response logging
 * - Error handling
 * - Type-safe API calls
 *
 * @example
 * // Simple GET request
 * const data = await apiClient.get<UserData>('/api/user');
 *
 * // POST with data
 * const result = await apiClient.post<JobResponse>('/api/job', {
 *   file: 'example.pdf'
 * });
 *
 * // With query params
 * const jobs = await apiClient.get<Job[]>('/api/jobs', {
 *   params: { status: 'completed' }
 * });
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import StorageService from './StorageService';
import { logInfo } from '../utils/logging';
import { TokenResponse } from '../types/api';
import { config } from '../config/env.config';

/**
 * API Client Class
 * Singleton pattern to ensure single axios instance
 */
class ApiClient {
  private client: AxiosInstance;
  private isRefreshing: boolean = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  constructor() {
    this.client = axios.create({
      baseURL: config.api.url,
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors(): void {
    // Request interceptor - inject token
    this.client.interceptors.request.use(
      (config) => {
        const token = StorageService.getToken();
        if (token && config.headers) {
          config.headers['pwc-identity-token'] = token;
        }

        // Log request in development
        if (process.env.NODE_ENV === 'development') {
          logInfo(this.describeApiLifecycle(config.method, config.url, 'start'));
        }

        return config;
      },
      (error) => {
        console.error('Request interceptor error');
        return Promise.reject(error);
      }
    );

    // Response interceptor - handle 498 token expiration
    this.client.interceptors.response.use(
      (response) => {
        // Log response in development
        if (process.env.NODE_ENV === 'development') {
          logInfo(this.describeApiLifecycle(response.config.method, response.config.url, 'success'));
        }
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config;

        // Handle 498 - Token Expired
        if (error.response?.status === 498 && originalRequest) {
          if (!this.isRefreshing) {
            this.isRefreshing = true;

            try {
              logInfo('Autenticazione: token scaduto, avvio refresh');
              await this.refreshToken();

              // Notify all subscribers that token has been refreshed
              const newToken = StorageService.getToken();
              if (newToken) {
                this.refreshSubscribers.forEach((callback) => callback(newToken));
              }
              this.refreshSubscribers = [];

              this.isRefreshing = false;

              // Retry original request with new token
              if (originalRequest.headers && newToken) {
                originalRequest.headers['pwc-identity-token'] = newToken;
              }
              return this.client(originalRequest);
            } catch (refreshError) {
              this.isRefreshing = false;
              this.refreshSubscribers = [];

              console.error('Token refresh failed');

              // Clear auth and redirect to login
              StorageService.clearAuth();
              window.location.href = '/login';

              return Promise.reject(refreshError);
            }
          }

          // If already refreshing, queue this request
          return new Promise((resolve) => {
            this.refreshSubscribers.push((token: string) => {
              if (originalRequest.headers) {
                originalRequest.headers['pwc-identity-token'] = token;
              }
              resolve(this.client(originalRequest));
            });
          });
        }

        // Handle other errors
        if (error.response) {
          console.error(`API Error ${error.response.status}`);
        } else if (error.request) {
          console.error('No response received');
        } else {
          console.error('Request setup error');
        }

        return Promise.reject(error);
      }
    );
  }

  private describeApiLifecycle(
    method: string | undefined,
    url: string | undefined,
    phase: 'start' | 'success'
  ): string {
    const normalizedMethod = (method || 'REQUEST').toUpperCase();
    const target = this.describeApiTarget(url);
    const action = phase === 'start' ? 'avviata' : 'completata';
    return `API ${normalizedMethod} ${target}: ${action}`;
  }

  private describeApiTarget(url?: string): string {
    if (!url) return 'richiesta';

    const normalizedUrl = url.toLowerCase();

    if (normalizedUrl.includes('/upload-signed-url')) return 'upload-signed-url';
    if (normalizedUrl.includes('/download-signed-url')) return 'download-signed-url';
    if (normalizedUrl.includes('/api/v0/pdf-to-text')) return 'pdf-to-text';
    if (normalizedUrl.includes('/api/v0/doc-compare')) return 'doc-compare';
    if (normalizedUrl.includes('/api/v0/doc-similarity')) return 'doc-similarity';
    if (normalizedUrl.includes('/api/v0/p7m')) return 'p7m';
    if (normalizedUrl.includes('/api/v0/job')) return 'job-status';
    if (normalizedUrl.includes('/api/v0/auth/authorization')) return 'auth-authorization';
    if (normalizedUrl.includes('/api/v0/auth/token')) return 'auth-token';
    if (normalizedUrl.includes('/api/v0/auth/refresh-token')) return 'auth-refresh-token';

    return 'richiesta';
  }

  /**
   * Refresh the access token
   * @throws Error if refresh fails
   */
  private async refreshToken(): Promise<void> {
    const refreshToken = StorageService.getRefreshToken();

    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      // Call refresh endpoint directly (without interceptor to avoid recursion)
      const response = await axios.get<TokenResponse>(
        config.api.endpoints.authRefresh,
        {
          headers: {
            'pwc-identity-refresh-token': refreshToken,
          },
        }
      );

      if (!response.data || !response.data.access_token) {
        throw new Error('Invalid token refresh response');
      }

      // Store new tokens
      StorageService.setToken(response.data.access_token);
      if (response.data.refresh_token) {
        StorageService.setRefreshToken(response.data.refresh_token);
      }

      logInfo('Autenticazione: token aggiornato');
    } catch (error) {
      console.error('Token refresh failed');
      throw error;
    }
  }

  // ============================================
  // PUBLIC API METHODS
  // ============================================

  /**
   * GET request
   * @param url - Endpoint URL
   * @param config - Axios request config
   * @returns Response data
   */
  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config);
    return response.data;
  }

  async getText(url: string, config?: AxiosRequestConfig): Promise<string> {
    const response: AxiosResponse<string> = await this.client.get(url, {
      ...config,
      responseType: 'text',
    });
    return response.data;
  }

  async getBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response: AxiosResponse<Blob> = await this.client.get(url, {
      ...config,
      responseType: 'blob',
    });
    return response.data;
  }

  /**
   * POST request
   * @param url - Endpoint URL
   * @param data - Request body
   * @param config - Axios request config
   * @returns Response data
   */
  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, config);
    return response.data;
  }

  /**
   * PUT request
   * @param url - Endpoint URL
   * @param data - Request body
   * @param config - Axios request config
   * @returns Response data
   */
  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  /**
   * PATCH request
   * @param url - Endpoint URL
   * @param data - Request body
   * @param config - Axios request config
   * @returns Response data
   */
  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.patch(url, data, config);
    return response.data;
  }

  /**
   * DELETE request
   * @param url - Endpoint URL
   * @param config - Axios request config
   * @returns Response data
   */
  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }

  /**
   * Get the underlying axios instance
   * Use this for advanced scenarios (file uploads, custom configs, etc.)
   */
  getAxiosInstance(): AxiosInstance {
    return this.client;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();

// Export class for testing
export { ApiClient };
